import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Wrench, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PriceMismatchPanel = ({ details, onFixed }) => {
  const [fixing, setFixing] = useState(false);
  const items = details?.items || [];

  const fixAll = async () => {
    if (!window.confirm(`Set the quote price to the till price for all ${items.length} products?`)) return;
    setFixing(true);
    try {
      const { data } = await axios.post(`${API}/data-quality/price-mismatch/fix`);
      toast.success(`Fixed ${data.fixed} mismatched products${data.filled_blank ? ` (also filled in a blank quote price on ${data.filled_blank} others)` : ""}. Quotes now use the till price.`);
      onFixed();
    } catch (e) {
      toast.error("Could not fix prices");
    } finally {
      setFixing(false);
    }
  };

  return (
    <div data-testid="price-mismatch-panel">
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm text-gray-600" data-testid="price-mismatch-count">
          {items.length} products — the till price is kept, the customer's discount is taken off afterwards
        </span>
        <Button size="sm" onClick={fixAll} disabled={fixing || !items.length} className="gap-1" data-testid="price-mismatch-fix-all-btn">
          {fixing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
          Fix all (use till price)
        </Button>
      </div>
      <div className="max-h-96 overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">PLU</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-28 text-right">Till price</TableHead>
              <TableHead className="w-28 text-right">Quote price</TableHead>
              <TableHead className="w-24 text-right">Difference</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id} data-testid={`price-mismatch-row-${i.plu_code}`}>
                <TableCell className="font-mono text-blue-600">{i.plu_code}</TableCell>
                <TableCell className="max-w-xs truncate">{i.name}</TableCell>
                <TableCell className="text-right font-mono font-semibold">R {i.selling_price_incl_vat.toFixed(2)}</TableCell>
                <TableCell className="text-right font-mono text-gray-500">R {i.retail_price_level1.toFixed(2)}</TableCell>
                <TableCell className={`text-right font-mono ${i.difference > 0 ? "text-red-600" : "text-green-700"}`}>
                  {i.difference > 0 ? "+" : ""}{i.difference.toFixed(2)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
